<template>
    <div class="manage-vendors">

        <!-- ===================== PHONE STEP ===================== -->
        <div v-if="messageType === 'vendor_flow'"
             class="mv-text-message mv-phone-card">

            <div class="mv-phone-question"
                 v-html="formatPrompt(responseText)">
            </div>

            <div class="mv-phone-row">
                <Dropdown v-model="phoneForm.countryCode"
                          :options="countryCodeList"
                          optionValue="value"
                          optionLabel="text"
                          placeholder="Code"
                          filter
                          :disabled="disabled || phoneSubmitted"
                          appendTo="self"
                          class="mv-country-dropdown"
                          @change="onVendorCountryCodeChange" />

                <input v-model="phoneForm.phoneNumber"
                       type="text"
                       inputmode="numeric"
                       class="mv-phone-input"
                       :disabled="disabled || phoneSubmitted"
                       :maxlength="phoneLength === 10 ? phoneLength + 4 : phoneLength"
                       :placeholder="vendorPhonePlaceholder"
                       @input="onVendorPhoneInput" />
            </div>

            <div v-if="phoneError" class="mv-phone-error">
                {{ phoneError }}
            </div>

            <button type="button"
                    class="mv-btn mv-btn--primary mv-phone-submit"
                    :disabled="disabled || phoneSubmitted || !isVendorPhoneComplete()"
                    @click="submitVendorPhone">
                Continue
            </button>
        </div>

        <!-- ===================== PREVIEW / SUMMARY ===================== -->
        <div v-else-if="messageType === 'vendor_preview'"
             class="mv-summary-card">

            <div class="mv-summary-header">
                <div>
                    <div class="mv-header-title">{{ summaryTitle }}</div>
                    <div class="mv-header-subtitle">
                        {{ cleanResponseText || 'Please review before registering.' }}
                    </div>
                </div>
            </div>

            <div class="mv-summary-body">
                <label class="mv-edit-row">
                    <span class="mv-label">
                        Vendor Name <span class="mv-required">*</span>
                    </span>
                    <input :value="fullVendorName(vendorForm)"
                           type="text"
                           class="mv-input"
                           maxlength="150"
                           :disabled="disabled"
                           @input="onPreviewVendorNameInput" />
                </label>

                <label class="mv-edit-row">
                    <span class="mv-label">Company Name</span>
                    <input v-model.trim="vendorForm.companyName"
                           type="text"
                           class="mv-input"
                           :disabled="disabled" />
                </label>

                <label class="mv-edit-row">
                    <span class="mv-label">
                        Email <span class="mv-required">*</span>
                    </span>
                    <input v-model.trim="vendorForm.email"
                           type="email"
                           class="mv-input"
                           maxlength="200"
                           :disabled="disabled"
                           placeholder="Enter email" />
                </label>

                <div class="mv-edit-row">
                    <span class="mv-label">Mobile <span class="mv-required">*</span></span>
                    <div class="mv-preview-phone-row">
                        <!-- appendTo="self" keeps the dropdown panel inside the chatbot -->
                        <Dropdown v-model="vendorForm.countryCodeId"
                                  :options="previewCountryCodes"
                                  optionValue="value"
                                  optionLabel="text"
                                  placeholder="Code"
                                  filter
                                  :disabled="disabled"
                                  appendTo="self"
                                  class="mv-preview-country-dropdown"
                                  @change="onPreviewCountryCodeChange" />

                        <input v-model="vendorForm.phone"
                               type="text"
                               inputmode="numeric"
                               class="mv-input mv-preview-phone-input"
                               :disabled="disabled"
                               :maxlength="previewPhoneLength === 10 ? previewPhoneLength + 4 : previewPhoneLength"
                               :placeholder="previewVendorPhonePlaceholder"
                               @input="onPreviewPhoneInput" />
                    </div>
                </div>

                <!-- EDITABLE ASSIGN FIELD -->
                <label class="mv-edit-row">
                    <span class="mv-label">{{ assignLabel }}</span>
                    <input v-model.trim="vendorForm.assetName"
                           type="text"
                           class="mv-input"
                           :disabled="disabled" />
                </label>

                <div class="mv-edit-row mv-edit-row--top">
                    <span class="mv-label">
                        Work Type <span class="mv-required">*</span>
                    </span>
                    <div class="mv-multi" ref="workTypeDropdown">
                        <button type="button"
                                class="mv-multi__trigger"
                                :disabled="disabled"
                                @click="toggleDropdown('workType')">
                            <span>{{ workTypeDropdownLabel }}</span>
                            <span class="mv-chevron">{{ workTypeOpen ? '▲' : '▼' }}</span>
                        </button>

                        <div v-if="workTypeOpen" class="mv-multi__panel">
                            <label class="mv-check mv-check--all">
                                <input type="checkbox"
                                       :checked="allWorkTypesSelected"
                                       :disabled="disabled || !workTypeOptions.length"
                                       @change="toggleAllWorkTypes($event.target.checked)" />
                                <span>Select All</span>
                            </label>

                            <label v-for="item in workTypeOptions"
                                   :key="item.value"
                                   class="mv-check">
                                <input v-model="selectedWorkTypeIds"
                                       type="checkbox"
                                       :value="String(item.value)"
                                       :disabled="disabled"
                                       @change="onWorkTypeChange" />
                                <span>{{ item.text }}</span>
                            </label>

                            <div v-if="!workTypeOptions.length"
                                 class="mv-empty-inline">
                                No work types available.
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mv-edit-row mv-edit-row--top">
                    <span class="mv-label">
                        Service <span class="mv-required">*</span>
                    </span>
                    <div class="mv-multi" ref="serviceDropdown">
                        <button type="button"
                                class="mv-multi__trigger"
                                :disabled="disabled"
                                @click="toggleDropdown('service')">
                            <span>{{ serviceDropdownLabel }}</span>
                            <span class="mv-chevron">{{ serviceOpen ? '▲' : '▼' }}</span>
                        </button>

                        <div v-if="serviceOpen" class="mv-multi__panel">
                            <label class="mv-check mv-check--all">
                                <input type="checkbox"
                                       :checked="allServicesSelected"
                                       :disabled="disabled || !serviceOptions.length"
                                       @change="toggleAllServices($event.target.checked)" />
                                <span>Select All</span>
                            </label>

                            <label v-for="item in serviceOptions"
                                   :key="item.value"
                                   class="mv-check">
                                <input v-model="selectedServiceIds"
                                       type="checkbox"
                                       :value="String(item.value)"
                                       :disabled="disabled" />
                                <span>{{ item.text }}</span>
                            </label>

                            <div v-if="!serviceOptions.length"
                                 class="mv-empty-inline">
                                No services available.
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mv-edit-row">
                    <span class="mv-label">
                        Country <span class="mv-required">*</span>
                    </span>
                    <div ref="countryDropdown"
                         class="mv-search-dropdown"
                         :class="{ 'is-open': countryDropdownOpen }"
                         @click.stop>
                        <button type="button"
                                class="mv-search-dropdown__trigger"
                                :disabled="disabled"
                                :aria-expanded="countryDropdownOpen ? 'true' : 'false'"
                                @click="toggleLocationDropdown('country')">
                            <span :class="{ 'is-placeholder': !selectedCountryText }">
                                {{ selectedCountryText || 'Select...' }}
                            </span>
                            <span class="mv-search-dropdown__icons">
                                <span v-if="vendorForm.countryId"
                                      class="mv-search-dropdown__clear"
                                      role="button"
                                      aria-label="Clear country"
                                      @click.stop="clearLocationSelection('country')">×</span>
                                <svg viewBox="0 0 16 16" aria-hidden="true">
                                    <path d="m3 6 5 5 5-5" />
                                </svg>
                            </span>
                        </button>
                        <div v-if="countryDropdownOpen" class="mv-search-dropdown__panel">
                            <div class="mv-search-dropdown__search">
                                <input v-model="countrySearch"
                                       type="text"
                                       placeholder="Search..."
                                       aria-label="Search countries"
                                       :disabled="disabled"
                                       @click.stop />
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <circle cx="10.8" cy="10.8" r="6.8" />
                                    <path d="m16 16 5 5" />
                                </svg>
                            </div>
                            <div class="mv-search-dropdown__options">
                                <button v-for="item in filteredCountryOptions"
                                        :key="String(item.value)"
                                        type="button"
                                        class="mv-search-dropdown__option"
                                        :class="{ 'is-selected': String(vendorForm.countryId) === String(item.value) }"
                                        @click="selectLocationOption('country', item)">
                                    {{ item.text }}
                                </button>
                                <div v-if="!filteredCountryOptions.length"
                                     class="mv-search-dropdown__empty">
                                    No results found.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mv-edit-row">
                    <span class="mv-label">
                        State <span class="mv-required">*</span>
                    </span>
                    <div ref="stateDropdown"
                         class="mv-search-dropdown"
                         :class="{ 'is-open': stateDropdownOpen }"
                         @click.stop>
                        <button type="button"
                                class="mv-search-dropdown__trigger"
                                :disabled="disabled"
                                :aria-expanded="stateDropdownOpen ? 'true' : 'false'"
                                @click="toggleLocationDropdown('state')">
                            <span :class="{ 'is-placeholder': !selectedStateText }">
                                {{ selectedStateText || 'Select...' }}
                            </span>
                            <span class="mv-search-dropdown__icons">
                                <span v-if="vendorForm.stateId"
                                      class="mv-search-dropdown__clear"
                                      role="button"
                                      aria-label="Clear state"
                                      @click.stop="clearLocationSelection('state')">×</span>
                                <svg viewBox="0 0 16 16" aria-hidden="true">
                                    <path d="m3 6 5 5 5-5" />
                                </svg>
                            </span>
                        </button>
                        <div v-if="stateDropdownOpen" class="mv-search-dropdown__panel">
                            <div class="mv-search-dropdown__search">
                                <input v-model="stateSearch"
                                       type="text"
                                       placeholder="Search..."
                                       aria-label="Search states"
                                       :disabled="disabled"
                                       @click.stop />
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <circle cx="10.8" cy="10.8" r="6.8" />
                                    <path d="m16 16 5 5" />
                                </svg>
                            </div>
                            <div class="mv-search-dropdown__options">
                                <button v-for="item in filteredStateOptions"
                                        :key="String(item.value)"
                                        type="button"
                                        class="mv-search-dropdown__option"
                                        :class="{ 'is-selected': String(vendorForm.stateId) === String(item.value) }"
                                        @click="selectLocationOption('state', item)">
                                    {{ item.text }}
                                </button>
                                <div v-if="!filteredStateOptions.length"
                                     class="mv-search-dropdown__empty">
                                    No results found.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <label class="mv-edit-row">
                    <span class="mv-label">Street Address</span>
                    <input v-model.trim="vendorForm.address"
                           type="text"
                           class="mv-input"
                           :disabled="disabled" />
                </label>

                <label class="mv-edit-row">
                    <span class="mv-label">City</span>
                    <input v-model.trim="vendorForm.city"
                           type="text"
                           class="mv-input"
                           :disabled="disabled" />
                </label>

                <label class="mv-edit-row">
                    <span class="mv-label">Zip</span>
                    <input v-model.trim="vendorForm.zipCode"
                           type="text"
                           class="mv-input"
                           maxlength="10"
                           :disabled="disabled" />
                </label>

                <label class="mv-field">
                    <span class="mv-label">Description</span>
                    <textarea v-model.trim="vendorForm.description"
                              rows="3"
                              class="mv-textarea"
                              :disabled="disabled"></textarea>
                </label>

                <p v-if="formError" class="mv-error">{{ formError }}</p>
            </div>

            <div class="mv-actions mv-actions--stacked">
                <button type="button"
                        class="mv-btn mv-btn--primary"
                        :disabled="disabled"
                        @click="confirmVendor">
                    Confirm &amp; Register
                </button>

                <button type="button"
                        class="mv-btn mv-btn--cancel"
                        :disabled="disabled"
                        @click="onCancel">
                    Cancel
                </button>
            </div>
        </div>

        <!-- ===================== CREATED / FAILED ===================== -->
        <div v-else-if="messageType === 'vendor_created' || messageType === 'vendor_failed'"
             class="ticket-preview-card">

            <div class="ticket-preview-header"
                 :class="{ 'mv-error-header': isError }">
                <div>
                    <h4>
                        {{
 isError
                            ? 'Vendor Not Added'
                            : (isBoatOrAircraft ? 'Vendor Added!' : 'Service Provider Added!')
                        }}
                    </h4>
                    <p>
                        {{
 isError
                            ? 'Something went wrong.'
                            : (isBoatOrAircraft
                                ? 'The Vendor has been registered.'
                                : 'The Service Provider has been registered.')
                        }}
                    </p>
                </div>
            </div>

            <div class="ticket-preview-body">
                <div class="ticket-preview-row">
                    <span class="tp-label">Vendor Name</span>
                    <span class="tp-value">{{ fullVendorName(vendorData) || '—' }}</span>
                </div>
                <div class="ticket-preview-row">
                    <span class="tp-label">Company</span>
                    <span class="tp-value">{{ vendorData.companyName || '—' }}</span>
                </div>
                <div class="ticket-preview-row">
                    <span class="tp-label">Email</span>
                    <span class="tp-value">{{ vendorData.email || vendorData.Email || '—' }}</span>
                </div>
                <div class="ticket-preview-row">
                    <span class="tp-label">Phone Number</span>
                    <span class="tp-value">{{ createdVendorPhone }}</span>
                </div>
                <div class="ticket-preview-row">
                    <span class="tp-label">{{ assignLabel }}</span>
                    <span class="tp-value">{{ vendorData.assetName || '—' }}</span>
                </div>
                <div class="ticket-preview-row">
                    <span class="tp-label">Work Type</span>
                    <span class="tp-value">{{ vendorData.workTypeCategoryName || vendorData.workType || '—' }}</span>
                </div>
                <div class="ticket-preview-row">
                    <span class="tp-label">Service</span>
                    <span class="tp-value">{{ vendorData.serviceName || vendorData.serviceTitle || '—' }}</span>
                </div>

                <div v-if="isError" class="ticket-preview-error">
                    <p><strong>Error:</strong> {{ vendorData.error || 'Please try again.' }}</p>
                </div>
            </div>

            <div class="ticket-preview-footer">
                <span>{{ formatMMDDYYYY(createdDate) }}</span>
                <span :class="{ 'mv-error-text': isError, 'mv-saved': !isError }">
                    {{ isError ? 'Not Saved' : 'Saved' }}
                </span>
            </div>
        </div>

        <!-- ===================== LIST ===================== -->
        <div v-else-if="messageType === 'vendor_list'"
             class="ticket-preview-card">

            <div class="ticket-preview-header">
                <div>
                    <h4>{{ vendorData.heading || 'Available Vendors' }}</h4>
                    <p>{{ vendors.length }} vendor{{ vendors.length === 1 ? '' : 's' }} found</p>
                </div>
                <span v-if="vendors.length" class="ticket-preview-number">{{ vendors.length }}</span>
            </div>

            <div class="ticket-preview-body mv-list-body">
                <div v-if="!vendors.length" class="mv-empty">No vendors found.</div>

                <div v-for="(vendor, index) in vendors"
                     :key="vendor.vendorId || vendor.id || index"
                     class="mv-list-item"
                     :class="{ 'mv-list-item--last': index === vendors.length - 1 }">

                    <div class="mv-list-top">
                        <div class="mv-list-profile">
                            <div class="mv-list-avatar"
                                 :style="{ backgroundColor: getVendorAvatarColor(index) }">
                                {{ getVendorInitials(vendor) }}
                            </div>

                            <div class="mv-list-main">
                                <div class="mv-list-title">{{ fullVendorName(vendor) || 'Vendor' }}</div>
                                <div class="mv-list-meta">{{ vendor.email || '—' }}</div>
                            </div>
                        </div>

                        <div class="mv-list-actions">
                            <button type="button"
                                    class="mv-detail-btn"
                                    :disabled="disabled"
                                    @click="toggleVendorDetails(vendor, index)">
                                {{ isVendorDetailsOpen(vendor, index) ? 'Hide Details' : 'Details' }}
                            </button>

                            <button type="button"
                                    class="mv-status-btn"
                                    :class="isVendorActive(vendor) ? 'mv-status-btn--active' : 'mv-status-btn--inactive'"
                                    :disabled="disabled"
                                    @click="requestToggleStatus(vendor)">
                                {{ isVendorActive(vendor) ? 'Active' : 'Inactive' }}
                            </button>
                        </div>
                    </div>

                    <div v-if="isVendorDetailsOpen(vendor, index)"
                         class="mv-vendor-details">
                        <div class="mv-detail-row"><span>Work Type</span><strong>{{ vendor.workType || '—' }}</strong></div>
                        <div class="mv-detail-row"><span>Email</span><strong>{{ vendor.email || '—' }}</strong></div>
                        <div class="mv-detail-row">
                            <span>Phone</span>
                            <strong class="mv-phone-value">
                                {{ vendor.phone ? '+' + vendor.phone : '—' }}
                            </strong>
                        </div>
                        <div class="mv-detail-row"><span>Service</span><strong>{{ vendor.serviceTitle || '—' }}</strong></div>
                        <div class="mv-detail-row"><span>Company Name</span><strong>{{ vendor.companyName || '—' }}</strong></div>
                    </div>
                </div>
            </div>
        </div>

        <!-- ===================== TEXT ONLY ===================== -->
        <div v-else-if="textOnlyTypes.includes(messageType)"
             class="mv-text-message">
            {{ cleanResponseText }}
        </div>
    </div>
</template>

<script>
    import Dropdown from 'primevue/dropdown';
    import UserService from '@/service/user.service.js';
    import {
        findDefaultUsCountryCode,
        resolvePhoneLength,
    } from '@/helpers/phoneCountryHelpers.js';

    function formatMMDDYYYY(value) {
        if (!value) return '—';

        const text = String(value).trim();
        const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (iso) return `${iso[2]}-${iso[3]}-${iso[1]}`;

        const date = text.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
        if (date) return `${date[1].padStart(2, '0')}-${date[2].padStart(2, '0')}-${date[3]}`;

        return text;
    }

    // Keep the country code selected in the phone step so the Vendor Summary
    // shows the same selection instead of falling back to US.
    let lastVendorCountryCodeId = '';
    let lastVendorCountryCodeText = '';

    // ------------------------------------------------------------------
    // DRAFT STORE
    // Keeps whatever the user typed in the Vendor Summary form so it is NOT
    // lost when the component is re-created (chat re-render, going back,
    // closing/reopening the chatbot, or a failed "Confirm & Register").
    // It lives at module level (survives re-mounts) and in sessionStorage
    // (survives a page reload). It is cleared on success or Cancel.
    // ------------------------------------------------------------------
    const DRAFT_KEY = 'vendorFormDraft';
    const DRAFT_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour
    let vendorDraft = null;

    function readVendorDraft() {
        if (!vendorDraft) {
            try {
                const raw = sessionStorage.getItem(DRAFT_KEY);
                vendorDraft = raw ? JSON.parse(raw) : null;
            } catch (e) {
                vendorDraft = null;
            }
        }

        if (vendorDraft && Date.now() - Number(vendorDraft.savedAt || 0) > DRAFT_MAX_AGE_MS) {
            clearVendorDraft();
        }

        return vendorDraft;
    }

    function saveVendorDraft(draft) {
        vendorDraft = draft;
        try {
            sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
        } catch (e) {
            /* storage unavailable - in-memory draft still works */
        }
    }

    function clearVendorDraft() {
        vendorDraft = null;
        try {
            sessionStorage.removeItem(DRAFT_KEY);
        } catch (e) {
            /* ignore */
        }
    }

    const blankVendorForm = () => ({
        vendorName: '',
        firstName: '',
        lastName: '',
        companyName: '',
        email: '',
        countryCodeId: '',
        countryCodeText: '',
        phone: '',
        assetId: '',
        assetName: '',
        workTypeCategoryId: '',
        workTypeCategoryName: '',
        serviceId: '',
        serviceName: '',
        stateId: '',
        stateName: '',
        countryId: '',
        countryName: '',
        address: '',
        city: '',
        zipCode: '',
        description: '',
    });

    export default {
        name: 'ManageVendors',

        components: { Dropdown },

        props: {
            msg: { type: Object, required: true },
            disabled: { type: Boolean, default: false },
            currentDate: { type: String, default: '' },
        },

        emits: ['confirm-vendor', 'cancel', 'toggle-status', 'phone-submit', 'phone-validity-change'],

        data() {
            return {
                vendorForm: blankVendorForm(),
                selectedWorkTypeIds: [],
                selectedServiceIds: [],
                loadedServices: [],
                workTypeOpen: false,
                serviceOpen: false,
                countryCodeList: [],
                countryMasterList: [],
                phoneForm: { countryCode: '', phoneNumber: '' },
                phoneLength: 10,
                phoneError: '',
                phoneSubmitted: false,
                previewPhoneLength: 10,
                formError: '',
                countryDropdownOpen: false,
                stateDropdownOpen: false,
                countrySearch: '',
                stateSearch: '',
                expandedVendorKey: '',
                // true while the form is being (re)built, so the draft
                // watchers do not save an untouched/prefilled form
                isInitializing: false,
                // last message we built the form from (prevents re-reset)
                lastInitSignature: '',
                textOnlyTypes: [
                    'vendor_cancelled',
                    'vendor_list_failed',
                    'vendor_status_updated',
                    'vendor_status_update_failed',
                ],
            };
        },

        async created() {
            await this.GetCountryCodeList();
            await this.GetCountryList();
        },

        mounted() {
            document.addEventListener('click', this.handleDropdownOutsideClick);
        },

        beforeUnmount() {
            document.removeEventListener('click', this.handleDropdownOutsideClick);
        },

        computed: {
            // ========== Asset type based labels ==========
            assetTypeKey() {
                const t = String(localStorage.getItem('assetType') || '').trim().toLowerCase();
                if (t === 'boat' || t === 'boatdera') return 'boat';
                if (t === 'aircraft' || t === 'plane' || t === 'planedera') return 'aircraft';
                return 'default';
            },
            isBoatOrAircraft() {
                return this.assetTypeKey === 'boat' || this.assetTypeKey === 'aircraft';
            },
            summaryTitle() {
                return this.isBoatOrAircraft
                    ? 'Vendor Summary'
                    : "Service Provider's Summary";
            },
            assignLabel() {
                if (this.assetTypeKey === 'boat') return 'Assign Boat';
                if (this.assetTypeKey === 'aircraft') return 'Assign Aircraft';
                return 'Assign Place';
            },
            // ==================================================

            payload() {
                return this.msg && this.msg.payload ? this.msg.payload : {};
            },

            vendorData() {
                return this.payload.vendor_data || {};
            },

            prefilled() {
                return this.vendorData.prefilled || this.vendorData || {};
            },

            vendors() {
                return Array.isArray(this.vendorData.vendors) ? this.vendorData.vendors : [];
            },

            messageType() {
                return this.msg?.type || this.msg?.Intent || '';
            },

            responseText() {
                return this.payload.response
                    || this.payload.formal_response
                    || this.msg?.response
                    || this.msg?.formal_response
                    || this.msg?.text
                    || '';
            },

            cleanResponseText() {
                return String(this.responseText || '').replace(/\*/g, '').trim();
            },
            isVendorPhonePrompt() {
                if (this.messageType !== 'vendor_flow') return false;

                const text = String(this.responseText || '')
                    .replace(/<[^>]*>/g, ' ')
                    .replace(/\*\*/g, '')
                    .toLowerCase();

                return text.includes('phone number')
                    || text.includes('phone no')
                    || text.includes('mobile number')
                    || text.includes('mobile')
                    || text.includes('contact number')
                    || text.includes('contact no')
                    || text.includes('phone');
            },

            isError() {
                return this.messageType === 'vendor_failed';
            },

            createdDate() {
                return this.vendorData.createdOn
                    || this.vendorData.createdDate
                    || this.vendorData.createdAt
                    || this.currentDate
                    || '';
            },

            createdVendorPhone() {
                const rawPhone =
                    this.vendorData.phone ||
                    this.vendorData.phoneNumber ||
                    this.vendorData.Phone ||
                    this.vendorData.PhoneNumber ||
                    localStorage.getItem('vendorSelectedPhoneNumber') ||
                    '';

                if (!rawPhone) return '—';

                const countryCodeText =
                    this.vendorData.countryCodeText ||
                    this.vendorData.CountryCodeText ||
                    localStorage.getItem('vendorSelectedCountryCodeText') ||
                    lastVendorCountryCodeText ||
                    '';

                return this.formatVendorPhoneWithCountry(
                    rawPhone,
                    countryCodeText
                );
            },

            vendorPhonePlaceholder() {
                const selectedCountryCode = (this.countryCodeList || []).find(
                    item => String(item.value) === String(this.phoneForm.countryCode)
                );

                const countryCodeText =
                    selectedCountryCode?.text ||
                    localStorage.getItem('vendorSelectedCountryCodeText') ||
                    '';

                return this.buildVendorPhonePlaceholder(
                    this.phoneLength || 10,
                    countryCodeText
                );
            },

            previewVendorPhonePlaceholder() {
                const selectedCountryCode = (this.previewCountryCodes || []).find(
                    item => String(item.value) === String(this.vendorForm.countryCodeId)
                );

                const countryCodeText =
                    selectedCountryCode?.text ||
                    this.vendorForm.countryCodeText ||
                    localStorage.getItem('vendorSelectedCountryCodeText') ||
                    '';

                return this.buildVendorPhonePlaceholder(
                    this.previewPhoneLength || 10,
                    countryCodeText
                );
            },

            previewCountryCodes() {
                const fromPayload = this.vendorData.countryCodes || [];

                if (Array.isArray(fromPayload) && fromPayload.length) {
                    return fromPayload.map(item => ({
                        value: item.value ?? item.id,
                        text: item.text ?? item.name,
                    }));
                }

                return this.countryCodeList;
            },

            stateOptions() {
                return (Array.isArray(this.vendorData.states) ? this.vendorData.states : []).map(item => ({
                    value: item.value ?? item.id,
                    text: item.text ?? item.name,
                }));
            },

            filteredCountryOptions() {
                const query = String(this.countrySearch || '').trim().toLowerCase();
                if (!query) return this.countryOptions;
                return this.countryOptions.filter(item =>
                    String(item.text || '').toLowerCase().includes(query)
                );
            },

            filteredStateOptions() {
                const query = String(this.stateSearch || '').trim().toLowerCase();
                if (!query) return this.stateOptions;
                return this.stateOptions.filter(item =>
                    String(item.text || '').toLowerCase().includes(query)
                );
            },

            selectedCountryText() {
                const item = this.countryOptions.find(option =>
                    String(option.value) === String(this.vendorForm.countryId)
                );
                return item?.text || this.vendorForm.countryName || '';
            },

            selectedStateText() {
                const item = this.stateOptions.find(option =>
                    String(option.value) === String(this.vendorForm.stateId)
                );
                return item?.text || this.vendorForm.stateName || '';
            },

            countryOptions() {
                const source = this.countryMasterList.length
                    ? this.countryMasterList
                    : (Array.isArray(this.vendorData.countries) ? this.vendorData.countries : []);

                return source.map(item => ({
                    value:
                        item.value ??
                        item.id ??
                        item.Id ??
                        item.countryId ??
                        item.CountryId ??
                        '',
                    text:
                        item.text ??
                        item.name ??
                        item.Name ??
                        item.countryName ??
                        item.CountryName ??
                        ''
                })).filter(item => item.value || item.text);
            },

            workTypeOptions() {
                return (Array.isArray(this.vendorData.workTypeCategories) ? this.vendorData.workTypeCategories : []).map(item => ({
                    value: String(item.id ?? item.value ?? ''),
                    text: item.workTypeCategory ?? item.text ?? item.name ?? '',
                })).filter(item => item.value && item.text);
            },

            serviceOptions() {
                const source = this.loadedServices.length
                    ? this.loadedServices
                    : (Array.isArray(this.vendorData.services) ? this.vendorData.services : []);

                return source.map(item => ({
                    value: String(
                        item.id ??
                        item.Id ??
                        item.value ??
                        item.serviceId ??
                        item.ServiceId ??
                        ''
                    ),
                    text:
                        item.title ??
                        item.Title ??
                        item.text ??
                        item.name ??
                        item.serviceName ??
                        item.ServiceName ??
                        ''
                })).filter(item => item.value && item.text);
            },

            allWorkTypesSelected() {
                return this.workTypeOptions.length > 0
                    && this.workTypeOptions.every(item => this.selectedWorkTypeIds.includes(String(item.value)));
            },

            allServicesSelected() {
                return this.serviceOptions.length > 0
                    && this.serviceOptions.every(item => this.selectedServiceIds.includes(String(item.value)));
            },

            workTypeDropdownLabel() {
                if (!this.selectedWorkTypeIds.length) return 'Select work type...';

                if (this.selectedWorkTypeIds.length === 1) {
                    const selected = this.workTypeOptions.find(item =>
                        this.selectedWorkTypeIds.includes(String(item.value))
                    );
                    return selected ? selected.text : '1 selected';
                }

                return `${this.selectedWorkTypeIds.length} work types selected`;
            },

            serviceDropdownLabel() {
                if (!this.selectedServiceIds.length) return 'Select service...';

                if (this.selectedServiceIds.length === 1) {
                    const selected = this.serviceOptions.find(item =>
                        this.selectedServiceIds.includes(String(item.value))
                    );
                    return selected ? selected.text : '1 selected';
                }

                return `${this.selectedServiceIds.length} services selected`;
            },
        },

        watch: {
            // FIX 1: the old deep watcher rebuilt (and blanked) the form every
            // time the parent touched `msg`. Now the form is rebuilt ONLY when
            // the message content really changed.
            msg: {
                immediate: true,
                deep: true,
                handler() {
                    const signature = this.buildMessageSignature();
                    if (signature === this.lastInitSignature) return;

                    this.lastInitSignature = signature;
                    this.initializeFromMessage();
                },
            },

            // FIX 2: save every edit to the draft store.
            vendorForm: {
                deep: true,
                handler() { this.persistDraft(); },
            },
            selectedWorkTypeIds: {
                deep: true,
                handler() { this.persistDraft(); },
            },
            selectedServiceIds: {
                deep: true,
                handler() { this.persistDraft(); },
            },
            loadedServices: {
                deep: true,
                handler() { this.persistDraft(); },
            },
        },

        methods: {

            // ---------------- draft helpers ----------------
            buildMessageSignature() {
                try {
                    return `${this.messageType}|${JSON.stringify(this.vendorData)}|${this.responseText}`;
                } catch (e) {
                    return `${this.messageType}|${Date.now()}`;
                }
            },

            persistDraft() {
                if (this.messageType !== 'vendor_preview') return;
                if (this.isInitializing) return;

                saveVendorDraft({
                    savedAt: Date.now(),
                    form: { ...this.vendorForm },
                    workTypeIds: [...this.selectedWorkTypeIds],
                    serviceIds: [...this.selectedServiceIds],
                    loadedServices: Array.isArray(this.loadedServices)
                        ? [...this.loadedServices]
                        : [],
                });
            },

            onCancel() {
                clearVendorDraft();
                this.$emit('cancel');
            },

            // ---------------- existing helpers ----------------
            isVendorActive(vendor) {
                return (
                    vendor?.isActive === true ||
                    vendor?.IsActive === true ||
                    vendor?.isActive === 1 ||
                    vendor?.IsActive === 1 ||
                    String(vendor?.isActive ?? vendor?.IsActive ?? '')
                        .toLowerCase() === 'true'
                );
            },
            isVendorPhoneComplete() {
                const digits = String(this.phoneForm.phoneNumber || '')
                    .replace(/\D/g, '');

                return digits.length === Number(this.phoneLength || 10);
            },

            fullVendorName(source) {
                const data = source || {};

                const firstName = String(
                    data.firstName ||
                    data.FirstName ||
                    ''
                ).trim();

                const lastName = String(
                    data.lastName ||
                    data.LastName ||
                    ''
                ).trim();

                const combined = `${firstName} ${lastName}`.trim();

                return (
                    combined ||
                    String(
                        data.vendorName ||
                        data.VendorName ||
                        ''
                    ).trim()
                );
            },

            getVendorInitials(vendor) {
                const name = this.fullVendorName(vendor) || 'Vendor';

                return String(name)
                    .trim()
                    .split(/\s+/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map(part => part.charAt(0))
                    .join('')
                    .toUpperCase();
            },

            getVendorAvatarColor(index) {
                const colors = [
                    '#7c3aed',
                    '#2563eb',
                    '#e11d48',
                    '#d97706',
                    '#0891b2',
                    '#16a34a'
                ];

                return colors[Number(index || 0) % colors.length];
            },

            onPreviewVendorNameInput(event) {
                const fullName = String(event?.target?.value || '').trim();

                const parts = fullName
                    .split(/\s+/)
                    .filter(Boolean);

                this.vendorForm.vendorName = fullName;
                this.vendorForm.firstName = parts.shift() || '';
                this.vendorForm.lastName = parts.join(' ');
            },

            getVendorNameParts() {
                const explicitFirst = String(
                    this.vendorForm.firstName ||
                    this.prefilled.firstName ||
                    this.prefilled.FirstName ||
                    ''
                ).trim();

                const explicitLast = String(
                    this.vendorForm.lastName ||
                    this.prefilled.lastName ||
                    this.prefilled.LastName ||
                    ''
                ).trim();

                if (explicitFirst || explicitLast) {
                    return {
                        firstName: explicitFirst,
                        lastName: explicitLast,
                        fullName: `${explicitFirst} ${explicitLast}`.trim()
                    };
                }

                const fullName = String(
                    this.vendorForm.vendorName ||
                    this.prefilled.vendorName ||
                    this.prefilled.VendorName ||
                    ''
                ).trim();

                const parts = fullName.split(/\s+/).filter(Boolean);

                return {
                    firstName: parts.shift() || '',
                    lastName: parts.join(' '),
                    fullName
                };
            },

            validateVendorForm() {
                const fullName = this.fullVendorName(this.vendorForm);
                const email = String(this.vendorForm.email || '').trim();
                const phoneDigits = String(this.vendorForm.phone || '').replace(/\D/g, '');

                if (!fullName) return 'Vendor name is required.';
                if (!email) return 'Email is required.';
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                    return 'Please enter a valid email address.';
                }
                if (!this.vendorForm.countryCodeId) return 'Country code is required.';
                if (!phoneDigits.length) return 'Mobile number is required.';
                if (!this.vendorForm.assetId) return `${this.assignLabel} is required.`;
                if (!this.selectedWorkTypeIds.length) return 'Please select at least one work type.';
                if (!this.selectedServiceIds.length) return 'Please select at least one service.';
                if (!this.vendorForm.stateId) return 'State is required.';
                if (!this.vendorForm.countryId) return 'Country is required.';

                return '';
            },

            // Pre-select "United States" in the Country dropdown (same as the web form).
            // Only applies when no country has been chosen yet.
            applyDefaultCountry() {
                if (this.messageType !== 'vendor_preview') return;
                if (this.vendorForm.countryId) return;

                const options = this.countryOptions || [];
                if (!options.length) return;

                const exactUs = /^(united states( of america)?|usa|u\.s\.a?\.?|us)$/i;

                const us =
                    options.find(item => exactUs.test(String(item.text || '').trim())) ||
                    options.find(item => {
                        const text = String(item.text || '');
                        return /united states/i.test(text) && !/minor|outlying|virgin/i.test(text);
                    });

                if (us) {
                    this.vendorForm.countryId = us.value;
                    this.vendorForm.countryName = us.text;
                }
            },

            toggleLocationDropdown(type) {
                if (this.disabled) return;

                if (type === 'country') {
                    this.countryDropdownOpen = !this.countryDropdownOpen;
                    this.stateDropdownOpen = false;
                    this.countrySearch = '';
                } else {
                    this.stateDropdownOpen = !this.stateDropdownOpen;
                    this.countryDropdownOpen = false;
                    this.stateSearch = '';
                }
            },

            selectLocationOption(type, item) {
                if (type === 'country') {
                    this.vendorForm.countryId = item ? item.value : '';
                    this.vendorForm.countryName = item ? item.text : '';
                    this.countryDropdownOpen = false;
                    this.countrySearch = '';
                } else {
                    this.vendorForm.stateId = item ? item.value : '';
                    this.vendorForm.stateName = item ? item.text : '';
                    this.stateDropdownOpen = false;
                    this.stateSearch = '';
                }
            },

            clearLocationSelection(type) {
                this.selectLocationOption(type, null);
            },

            handleDropdownOutsideClick(event) {
                const workTypeDropdown = this.$refs.workTypeDropdown;
                const serviceDropdown = this.$refs.serviceDropdown;

                const clickedInsideWorkType =
                    workTypeDropdown &&
                    workTypeDropdown.contains(event.target);

                const clickedInsideService =
                    serviceDropdown &&
                    serviceDropdown.contains(event.target);

                if (!clickedInsideWorkType && !clickedInsideService) {
                    this.workTypeOpen = false;
                    this.serviceOpen = false;
                }

                const clickedInsideCountry = this.$refs.countryDropdown &&
                    this.$refs.countryDropdown.contains(event.target);
                const clickedInsideState = this.$refs.stateDropdown &&
                    this.$refs.stateDropdown.contains(event.target);

                if (!clickedInsideCountry) {
                    this.countryDropdownOpen = false;
                }
                if (!clickedInsideState) {
                    this.stateDropdownOpen = false;
                }
            },

            formatMMDDYYYY,

            formatPrompt(text) {
                const value = String(text || '');

                return value
                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                    .replace(/\*/g, '');
            },

            toIdArray(value) {
                if (Array.isArray(value)) return value.map(item => String(item)).filter(Boolean);

                return String(value || '')
                    .split(',')
                    .map(item => item.trim())
                    .filter(Boolean);
            },

            async loadVendorServices() {
                // FIX 3: remember the services the user already ticked so a
                // work-type change only removes the ones that no longer apply.
                const previousServiceIds = [...this.selectedServiceIds];
                this.formError = '';

                if (!this.selectedWorkTypeIds.length) {
                    this.loadedServices = [];
                    this.selectedServiceIds = [];
                    this.serviceOpen = false;
                    return;
                }

                const companyId =
                    localStorage.getItem('companyId') ||
                    this.vendorData.companyId ||
                    this.vendorData.CompanyId ||
                    '';

                if (!companyId) {
                    this.formError = 'Company Id is required to load services.';
                    this.serviceOpen = false;
                    return;
                }

                const workTypeIds = this.selectedWorkTypeIds
                    .map(item => String(item || '').trim())
                    .filter(Boolean)
                    .join(',');

                if (!workTypeIds) {
                    this.serviceOpen = false;
                    return;
                }

                try {
                    const response = await UserService.getVendorServicesList({
                        CompanyId: companyId,
                        WorkTypeIds: workTypeIds,
                    });

                    const result = response?.data;

                    const rows = Array.isArray(result)
                        ? result
                        : Array.isArray(result?.data)
                            ? result.data
                            : Array.isArray(result?.result)
                                ? result.result
                                : [];

                    this.loadedServices = rows
                        .map(item => ({
                            id:
                                item.id ??
                                item.Id ??
                                item.value ??
                                item.serviceId ??
                                item.ServiceId ??
                                '',
                            title:
                                item.title ??
                                item.Title ??
                                item.text ??
                                item.name ??
                                item.serviceName ??
                                item.ServiceName ??
                                '',
                            workTypeId:
                                item.workTypeId ??
                                item.WorkTypeId ??
                                null,
                        }))
                        .filter(item =>
                            String(item.id || '').trim() &&
                            String(item.title || '').trim()
                        );

                    // keep only the previously selected services that still exist
                    const validIds = this.loadedServices.map(item => String(item.id));
                    this.selectedServiceIds = previousServiceIds.filter(id => validIds.includes(String(id)));

                    this.workTypeOpen = false;

                    await this.$nextTick();

                    this.serviceOpen = true;

                } catch (error) {
                    console.error(
                        'GetVendorServicesList error:',
                        error?.response || error
                    );

                    // keep what the user had selected; just show the error
                    this.serviceOpen = true;
                    this.formError =
                        error?.response?.data?.responseMessage ||
                        error?.response?.data?.message ||
                        (
                            error?.response?.status
                                ? `Unable to load services (${error.response.status}).`
                                : 'Unable to load services.'
                        );
                }
            },

            async onWorkTypeChange() {
                await this.loadVendorServices();
            },

            initializeFromMessage() {
                // A finished / cancelled flow must not leave a stale draft behind.
                if (this.messageType === 'vendor_created' || this.messageType === 'vendor_cancelled') {
                    clearVendorDraft();
                    return;
                }

                if (this.messageType !== 'vendor_preview') return;

                this.isInitializing = true;

                const p = this.prefilled || {};

                this.vendorForm = {
                    ...blankVendorForm(),
                    ...p,
                    vendorName:
                        p.vendorName ||
                        p.VendorName ||
                        [p.firstName || p.FirstName, p.lastName || p.LastName]
                            .filter(Boolean)
                            .join(' ')
                            .trim() ||
                        '',
                    firstName: p.firstName || p.FirstName || '',
                    lastName: p.lastName || p.LastName || '',
                    companyName:
                        p.companyName ||
                        p.VendorCompanyName ||
                        p.CompanyName ||
                        '',
                    email: p.email || p.Email || '',
                    countryCodeId:
                        localStorage.getItem('vendorSelectedCountryCodeId') ||
                        lastVendorCountryCodeId ||
                        p.countryCodeId ||
                        '',
                    countryCodeText:
                        localStorage.getItem('vendorSelectedCountryCodeText') ||
                        lastVendorCountryCodeText ||
                        p.countryCodeText ||
                        '',
                    phone: p.phone || p.Phone || p.PhoneNumber || '',
                    assetId: p.assetId || p.assetID || p.AssignPropId || '',
                    assetName: p.assetName || '',
                    workTypeCategoryId: p.workTypeCategoryId || p.WorkTypeId || '',
                    workTypeCategoryName: p.workTypeCategoryName || '',
                    serviceId: p.serviceId || p.ServiceId || '',
                    serviceName: p.serviceName || '',
                    stateId: p.stateId || p.state || p.State || '',
                    stateName: p.stateName || '',
                    countryId: p.countryId || p.country || p.Country || '',
                    countryName: p.countryName || '',
                    address: p.address || p.Address || '',
                    city: p.city || p.City || '',
                    zipCode: p.zipCode || p.ZipCode || '',
                    description: p.description || p.Description || '',
                };

                this.selectedWorkTypeIds = this.toIdArray(p.workTypeCategoryId);
                this.selectedServiceIds = this.toIdArray(p.serviceId);
                this.loadedServices = Array.isArray(this.vendorData.services)
                    ? [...this.vendorData.services]
                    : [];

                // FIX 4: restore what the user already typed (if any).
                const draft = readVendorDraft();
                if (draft && draft.form) {
                    this.vendorForm = { ...this.vendorForm, ...draft.form };
                    this.selectedWorkTypeIds = Array.isArray(draft.workTypeIds)
                        ? [...draft.workTypeIds]
                        : this.selectedWorkTypeIds;
                    this.selectedServiceIds = Array.isArray(draft.serviceIds)
                        ? [...draft.serviceIds]
                        : this.selectedServiceIds;
                    if (Array.isArray(draft.loadedServices) && draft.loadedServices.length) {
                        this.loadedServices = [...draft.loadedServices];
                    }
                }

                this.workTypeOpen = false;
                this.serviceOpen = false;
                this.formError = '';

                this.previewPhoneLength =
                    resolvePhoneLength(this.vendorForm.countryCodeId, this.previewCountryCodes) || 10;

                const selectedPreviewCountryCode = (this.previewCountryCodes || []).find(
                    item => String(item.value) === String(this.vendorForm.countryCodeId)
                );

                const previewCountryCodeText =
                    selectedPreviewCountryCode?.text ||
                    this.vendorForm.countryCodeText ||
                    localStorage.getItem('vendorSelectedCountryCodeText') ||
                    lastVendorCountryCodeText ||
                    '';

                const previewDigits = this.normalizeVendorLocalPhoneDigits(
                    this.vendorForm.phone,
                    previewCountryCodeText,
                    this.previewPhoneLength || 10
                );

                this.vendorForm.phone = this.formatVendorLocalPhone(
                    previewDigits,
                    previewCountryCodeText
                );

                // Default Country = United States when nothing was prefilled
                this.applyDefaultCountry();

                // let the watchers flush, then allow draft saving again
                this.$nextTick(() => {
                    this.isInitializing = false;
                });
            },

            toggleDropdown(type) {
                if (this.disabled) return;

                if (type === 'workType') {
                    this.workTypeOpen = !this.workTypeOpen;
                    this.serviceOpen = false;
                } else {
                    this.serviceOpen = !this.serviceOpen;
                    this.workTypeOpen = false;
                }
            },

            async toggleAllWorkTypes(checked) {
                this.selectedWorkTypeIds = checked
                    ? this.workTypeOptions.map(item =>
                        String(item.value)
                    )
                    : [];

                this.formError = '';

                if (!checked || !this.selectedWorkTypeIds.length) {
                    this.selectedServiceIds = [];
                    this.loadedServices = [];
                    this.workTypeOpen = false;
                    this.serviceOpen = false;
                    return;
                }

                await this.loadVendorServices();
            },

            toggleAllServices(checked) {
                this.selectedServiceIds = checked
                    ? this.serviceOptions.map(item => String(item.value))
                    : [];
            },

            selectedNames(ids, options) {
                return ids.map(id => {
                    const item = options.find(option => String(option.value) === String(id));
                    return item ? item.text : '';
                }).filter(Boolean).join(', ');
            },

            async GetCountryList() {
                try {
                    const response = await UserService.countryList(
                        'country',
                        '00Test',
                        localStorage.getItem('companyId')
                    );

                    const result = response?.data;

                    this.countryMasterList = Array.isArray(result)
                        ? result
                        : Array.isArray(result?.data)
                            ? result.data
                            : Array.isArray(result?.result)
                                ? result.result
                                : [];
                } catch (e) {
                    console.error('Vendor country list load failed', e);
                    this.countryMasterList = [];
                }

                // Country list is now loaded, so the US default can be applied
                this.applyDefaultCountry();
            },

            async GetCountryCodeList() {
                try {
                    const response = await UserService.countryList(
                        'countryCode',
                        '00Test',
                        localStorage.getItem('companyId')
                    );

                    this.countryCodeList = response.data || [];

                    const us = findDefaultUsCountryCode(this.countryCodeList)
                        || this.countryCodeList.find((item) =>
                            String(item.text || '').includes('US')
                        );

                    if (us) {
                        if (!this.phoneForm.countryCode) {
                            this.phoneForm.countryCode = us.value;
                        }

                        this.phoneLength =
                            resolvePhoneLength(
                                this.phoneForm.countryCode,
                                this.countryCodeList
                            ) || 10;

                        if (
                            this.messageType === 'vendor_preview' &&
                            !this.vendorForm.countryCodeId
                        ) {
                            const rememberedCode =
                                localStorage.getItem('vendorSelectedCountryCodeId') ||
                                lastVendorCountryCodeId ||
                                us.value;

                            const rememberedText =
                                localStorage.getItem('vendorSelectedCountryCodeText') ||
                                lastVendorCountryCodeText ||
                                '';

                            this.vendorForm.countryCodeId = rememberedCode;
                            this.vendorForm.countryCodeText =
                                rememberedText ||
                                this.previewCountryCodes.find(
                                    item => String(item.value) === String(rememberedCode)
                                )?.text ||
                                '';
                            this.previewPhoneLength =
                                resolvePhoneLength(
                                    rememberedCode,
                                    this.previewCountryCodes
                                ) || 10;
                        }

                        if (
                            this.messageType === 'vendor_preview' &&
                            this.vendorForm.phone
                        ) {
                            const selectedPreviewCountryCode = (this.previewCountryCodes || []).find(
                                item => String(item.value) === String(this.vendorForm.countryCodeId)
                            );

                            const previewCountryCodeText =
                                selectedPreviewCountryCode?.text ||
                                this.vendorForm.countryCodeText ||
                                localStorage.getItem('vendorSelectedCountryCodeText') ||
                                lastVendorCountryCodeText ||
                                '';

                            const previewDigits = this.normalizeVendorLocalPhoneDigits(
                                this.vendorForm.phone,
                                previewCountryCodeText,
                                this.previewPhoneLength || 10
                            );

                            this.vendorForm.phone = this.formatVendorLocalPhone(
                                previewDigits,
                                previewCountryCodeText
                            );
                        }
                    }
                } catch (e) {
                    console.error('Vendor country code load failed', e);
                    this.countryCodeList = [];
                }
            },

            onVendorCountryCodeChange() {
                this.phoneLength =
                    resolvePhoneLength(this.phoneForm.countryCode, this.countryCodeList) || 10;

                const selectedCountryCode = (this.countryCodeList || []).find(
                    item => String(item.value) === String(this.phoneForm.countryCode)
                );

                lastVendorCountryCodeId = this.phoneForm.countryCode || '';
                lastVendorCountryCodeText = selectedCountryCode?.text || '';

                localStorage.setItem(
                    'vendorSelectedCountryCodeId',
                    lastVendorCountryCodeId
                );
                localStorage.setItem(
                    'vendorSelectedCountryCodeText',
                    lastVendorCountryCodeText
                );

                this.phoneForm.phoneNumber = '';
                this.phoneError = '';

                this.$emit('phone-validity-change', false);
            },

            onVendorPhoneInput(event) {
                const maxLength = this.phoneLength || 10;

                const selectedCountryCode = (this.countryCodeList || []).find(
                    item => String(item.value) === String(this.phoneForm.countryCode)
                );

                const countryCodeText =
                    selectedCountryCode?.text ||
                    localStorage.getItem('vendorSelectedCountryCodeText') ||
                    '';

                const digits = this.normalizeVendorLocalPhoneDigits(
                    event?.target?.value,
                    countryCodeText,
                    maxLength
                );

                this.phoneForm.phoneNumber = this.formatVendorLocalPhone(
                    digits,
                    countryCodeText
                );

                this.phoneError = '';

                this.$emit(
                    'phone-validity-change',
                    this.isVendorPhoneComplete()
                );
            },

            onPreviewCountryCodeChange() {
                this.previewPhoneLength =
                    resolvePhoneLength(this.vendorForm.countryCodeId, this.previewCountryCodes) || 10;

                this.vendorForm.phone = '';
            },

            onPreviewPhoneInput(event) {
                const maxLength = this.previewPhoneLength || 10;

                const selectedCountryCode = (this.previewCountryCodes || []).find(
                    item => String(item.value) === String(this.vendorForm.countryCodeId)
                );

                const countryCodeText =
                    selectedCountryCode?.text ||
                    this.vendorForm.countryCodeText ||
                    localStorage.getItem('vendorSelectedCountryCodeText') ||
                    '';

                const digits = this.normalizeVendorLocalPhoneDigits(
                    event?.target?.value,
                    countryCodeText,
                    maxLength
                );

                this.vendorForm.phone = this.formatVendorLocalPhone(
                    digits,
                    countryCodeText
                );
            },

            buildVendorPhonePlaceholder(maxLength, countryCodeText) {
                const length = Number(maxLength || 10);
                const dialCode = this.getVendorDialCode(countryCodeText);

                if (dialCode === '+1' && length === 10) {
                    return '(000)-000-0000';
                }

                if (/^\+1-[0-9]{3}$/.test(dialCode) && length === 7) {
                    return '000-0000';
                }

                if (length === 10) {
                    return '(000)-000-0000';
                }

                if (length === 7) {
                    return '000-0000';
                }

                if (length > 0) {
                    return '0'.repeat(length);
                }

                return 'Phone number';
            },

            formatPhoneDigits(digits, maxLength) {
                const cleanDigits = String(digits || '').replace(/\D/g, '');

                if (maxLength === 10) {
                    if (cleanDigits.length > 6) {
                        return `(${cleanDigits.slice(0, 3)})-${cleanDigits.slice(3, 6)}-${cleanDigits.slice(6)}`;
                    }

                    if (cleanDigits.length > 3) {
                        return `(${cleanDigits.slice(0, 3)})-${cleanDigits.slice(3)}`;
                    }

                    if (cleanDigits.length > 0) {
                        return `(${cleanDigits}`;
                    }
                }

                return cleanDigits;
            },

            getVendorDialCode(countryCodeText) {
                const text = String(countryCodeText || '').trim();
                const match = text.match(/^\+[0-9]+(?:-[0-9]+)*/);
                return match ? match[0] : '';
            },

            normalizeVendorLocalPhoneDigits(value, countryCodeText, maxLength) {
                let digits = String(value || '').replace(/\D/g, '');
                const dialCode = this.getVendorDialCode(countryCodeText);
                const dialDigits = dialCode.replace(/\D/g, '');

                if (
                    dialDigits &&
                    digits.length > maxLength &&
                    digits.startsWith(dialDigits)
                ) {
                    digits = digits.slice(dialDigits.length);
                }

                const nanpMatch = dialCode.match(/^\+1-([0-9]{3})$/);
                if (nanpMatch) {
                    const areaCode = nanpMatch[1];

                    if (
                        digits.length > 7 &&
                        digits.startsWith(areaCode)
                    ) {
                        digits = digits.slice(3);
                    }
                }

                return digits.slice(0, maxLength);
            },

            formatVendorLocalPhone(digits, countryCodeText) {
                const cleanDigits = String(digits || '').replace(/\D/g, '');
                const dialCode = this.getVendorDialCode(countryCodeText);

                if (dialCode === '+1' && cleanDigits.length === 10) {
                    return `(${cleanDigits.slice(0, 3)})-${cleanDigits.slice(3, 6)}-${cleanDigits.slice(6)}`;
                }

                if (/^\+1-[0-9]{3}$/.test(dialCode) && cleanDigits.length === 7) {
                    return `${cleanDigits.slice(0, 3)}-${cleanDigits.slice(3)}`;
                }

                if (cleanDigits.length === 10) {
                    return `(${cleanDigits.slice(0, 3)})-${cleanDigits.slice(3, 6)}-${cleanDigits.slice(6)}`;
                }

                if (cleanDigits.length === 7) {
                    return `${cleanDigits.slice(0, 3)}-${cleanDigits.slice(3)}`;
                }

                return cleanDigits;
            },

            formatVendorPhoneWithCountry(phone, countryCodeText) {
                const dialCode = this.getVendorDialCode(countryCodeText);

                let digits = String(phone || '').replace(/\D/g, '');
                const dialDigits = dialCode.replace(/\D/g, '');

                if (
                    dialDigits &&
                    digits.length > 10 &&
                    digits.startsWith(dialDigits)
                ) {
                    digits = digits.slice(dialDigits.length);
                }

                const nanpMatch = dialCode.match(/^\+1-([0-9]{3})$/);
                if (nanpMatch) {
                    const areaCode = nanpMatch[1];

                    if (
                        digits.length > 7 &&
                        digits.startsWith(areaCode)
                    ) {
                        digits = digits.slice(3);
                    }
                }

                const localFormatted = this.formatVendorLocalPhone(
                    digits,
                    countryCodeText
                );

                return dialCode
                    ? `${dialCode} ${localFormatted}`.trim()
                    : localFormatted;
            },

            resolveVendorDialCode() {
                const value = this.phoneForm.countryCode;

                if (!value) return '';

                const selected = (this.countryCodeList || [])
                    .find((item) => String(item.value) === String(value));

                if (!selected || !selected.text) {
                    return value;
                }

                const dialCode =
                    String(selected.text).trim().split(/\s+/)[0];

                return dialCode || value;
            },

            submitVendorPhone() {
                this.phoneError = '';

                if (!this.phoneForm.countryCode) {
                    this.phoneError =
                        'Please select a country code.';
                    return;
                }

                const digits =
                    String(this.phoneForm.phoneNumber || '')
                        .replace(/\D/g, '');

                const requiredLength =
                    this.phoneLength || 10;

                if (digits.length !== requiredLength) {
                    this.phoneError =
                        `Enter a valid ${requiredLength}-digit phone number.`;
                    return;
                }

                const dialCode =
                    this.resolveVendorDialCode();

                const selectedCountryCodeForFormat = (this.countryCodeList || []).find(
                    item => String(item.value) === String(this.phoneForm.countryCode)
                );

                const countryCodeTextForFormat =
                    selectedCountryCodeForFormat?.text ||
                    lastVendorCountryCodeText ||
                    localStorage.getItem('vendorSelectedCountryCodeText') ||
                    '';

                const formattedPhone = this.formatVendorLocalPhone(
                    digits,
                    countryCodeTextForFormat
                );

                const fullPhoneNumber = dialCode
                    ? `${dialCode} ${formattedPhone}`
                    : formattedPhone;

                const selectedCountryCode = (this.countryCodeList || [])
                    .find(item =>
                        String(item.value) === String(this.phoneForm.countryCode)
                    );

                lastVendorCountryCodeId = this.phoneForm.countryCode || '';
                lastVendorCountryCodeText = selectedCountryCode?.text || '';

                localStorage.setItem(
                    'vendorSelectedCountryCodeId',
                    lastVendorCountryCodeId
                );
                localStorage.setItem(
                    'vendorSelectedCountryCodeText',
                    lastVendorCountryCodeText
                );

                localStorage.setItem(
                    'vendorSelectedPhoneNumber',
                    formattedPhone
                );

                this.phoneSubmitted = true;

                this.$emit(
                    'phone-submit',
                    fullPhoneNumber
                );
            },

            confirmVendor() {
                this.formError = '';

                const validationError = this.validateVendorForm();
                if (validationError) {
                    this.formError = validationError;
                    return;
                }

                const countryCode = this.previewCountryCodes.find(item =>
                    String(item.value) === String(this.vendorForm.countryCodeId)
                );
                const state = this.stateOptions.find(item =>
                    String(item.value) === String(this.vendorForm.stateId)
                );
                const country = this.countryOptions.find(item =>
                    String(item.value) === String(this.vendorForm.countryId)
                );

                const selectedCountryCodeId =
                    localStorage.getItem('vendorSelectedCountryCodeId') ||
                    this.vendorForm.countryCodeId ||
                    '';

                const selectedCountryCodeText =
                    localStorage.getItem('vendorSelectedCountryCodeText') ||
                    countryCode?.text ||
                    this.vendorForm.countryCodeText ||
                    '';

                const name = this.getVendorNameParts();

                const workTypeIds = this.selectedWorkTypeIds.join(',');
                const workTypeNames =
                    this.selectedNames(this.selectedWorkTypeIds, this.workTypeOptions);

                const serviceIds = this.selectedServiceIds.join(',');
                const serviceNames =
                    this.selectedNames(this.selectedServiceIds, this.serviceOptions);

                const payload = {
                    ...this.prefilled,
                    ...this.vendorForm,

                    vendorName: name.fullName,
                    firstName: name.firstName,
                    lastName: name.lastName,
                    countryCodeId: selectedCountryCodeId,
                    countryCodeText: selectedCountryCodeText,
                    stateName: state?.text || this.vendorForm.stateName || '',
                    countryName: country?.text || this.vendorForm.countryName || '',
                    workTypeCategoryId: workTypeIds,
                    workTypeCategoryName: workTypeNames,
                    serviceId: serviceIds,
                    serviceName: serviceNames,

                    VendorName: name.fullName,
                    FirstName: name.firstName,
                    LastName: name.lastName,
                    VendorCompanyName: this.vendorForm.companyName || '',
                    Email: this.vendorForm.email || '',
                    UserName: this.vendorForm.email || '',
                    Phone: this.vendorForm.phone || '',
                    AssignPropId: this.vendorForm.assetId || '',
                    WorkTypeId: workTypeIds,
                    ServiceId: serviceIds,
                    state: this.vendorForm.stateId || '',
                    countryCode: selectedCountryCodeId,
                    address: this.vendorForm.address || '',
                    city: this.vendorForm.city || '',
                    zipCode: this.vendorForm.zipCode || '',
                    Description: this.vendorForm.description || ''
                };

                // Draft is intentionally kept here: if registration fails the
                // user comes back to a filled form. It is cleared when the
                // message type becomes vendor_created / vendor_cancelled.
                this.$emit('confirm-vendor', payload);
            },

            vendorKey(vendor, index) {
                return String(vendor.vendorId || vendor.id || index);
            },

            toggleVendorDetails(vendor, index) {
                const key = this.vendorKey(vendor, index);
                this.expandedVendorKey = this.expandedVendorKey === key ? '' : key;
            },

            isVendorDetailsOpen(vendor, index) {
                return this.expandedVendorKey === this.vendorKey(vendor, index);
            },

            requestToggleStatus(vendor) {
                if (!vendor) return;

                this.$emit('toggle-status', {
                    vendorId:
                        vendor.vendorId ||
                        vendor.VendorId ||
                        vendor.id ||
                        vendor.Id ||
                        '',
                    vendorName:
                        this.fullVendorName(vendor) ||
                        'Vendor',
                    isActive: !this.isVendorActive(vendor),
                });
            },
        },
    };
</script>

<style scoped>
    .manage-vendors {
        width: 100%;
        font-family: Outfit, sans-serif;
        /* FIX: the dropdowns below use very high z-index values. Without a
           stacking context they paint ABOVE the chatbot footer / input bar.
           isolation: isolate keeps every inner z-index inside this component. */
        position: relative;
        isolation: isolate;
        z-index: 0;
    }

    .mv-summary-card,
    .ticket-preview-card {
        background: #fff;
        border-radius: 16px;
        box-shadow: 0 4px 18px rgba(0, 0, 0, 0.12);
        overflow: hidden;
        font-size: 13px;
        min-width: 300px;
        max-width: 360px;
    }

    /* Allow searchable location dropdown panels to extend beyond the card. */
    .mv-summary-card {
        position: relative;
        overflow: visible;
    }

        .mv-summary-card .mv-summary-header {
            border-radius: 16px 16px 0 0;
        }

    .mv-summary-header,
    .ticket-preview-header {
        background: linear-gradient(135deg, #0f66b8 0%, #122341 100%);
        color: #fff;
        padding: 14px 18px;
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .mv-error-header {
        background: linear-gradient(135deg, #991b1b, #dc2626) !important;
    }

    .mv-header-title,
    .ticket-preview-header h4 {
        margin: 0;
        color: #fff;
        font-weight: 700;
        font-size: 14px;
    }

    .mv-header-subtitle,
    .ticket-preview-header p {
        margin: 2px 0 0;
        color: #fff;
        font-size: 11px;
        opacity: 0.82;
    }

    .mv-summary-body,
    .ticket-preview-body {
        padding: 14px 18px;
    }

    .mv-row,
    .mv-edit-row,
    .ticket-preview-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 10px;
        border-bottom: 1px solid #f0f0f0;
        padding-bottom: 8px;
        margin-bottom: 8px;
        box-sizing: border-box;
    }

    .mv-edit-row--top {
        align-items: flex-start;
    }

    .mv-label,
    .tp-label {
        width: 96px;
        min-width: 96px;
        color: #667085;
        font-size: 11px;
        font-weight: 600;
        line-height: 1.4;
    }

    .mv-value,
    .tp-value {
        flex: 1;
        min-width: 0;
        color: #1e2d45;
        font-size: 12px;
        font-weight: 600;
        text-align: right;
    }

    .mv-value--wrap,
    .tp-value {
        overflow-wrap: anywhere;
    }

    .mv-input,
    .mv-form-dropdown,
    .mv-multi {
        width: 190px;
        min-width: 190px;
        max-width: 190px;
    }

    .mv-input,
    .mv-textarea {
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        background: #fff;
        color: #1e2d45;
        font-family: Outfit, sans-serif;
        font-size: 12px;
        box-sizing: border-box;
        outline: none;
    }

    .mv-input {
        height: 36px;
        padding: 7px 9px;
    }

        .mv-input:focus,
        .mv-textarea:focus {
            border-color: #0f66b8;
            box-shadow: 0 0 0 2px rgba(15, 102, 184, 0.1);
        }

    .mv-field {
        display: block;
        padding-top: 2px;
    }

        .mv-field .mv-label {
            display: block;
            width: auto;
            min-width: 0;
            margin-bottom: 5px;
        }

    .mv-textarea {
        width: 100%;
        min-height: 66px;
        padding: 8px 10px;
        resize: vertical;
    }

    .mv-actions {
        padding: 0 18px 16px;
        display: flex;
        gap: 8px;
    }

    .mv-actions--stacked {
        flex-direction: column;
    }

    .mv-btn {
        border-radius: 10px;
        padding: 10px;
        font-family: Outfit, sans-serif;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
    }

        .mv-btn:disabled,
        .mv-detail-btn:disabled,
        .mv-status-btn:disabled {
            opacity: 0.45;
            cursor: not-allowed;
            pointer-events: none;
        }

    .mv-btn--primary {
        border: 0;
        background: linear-gradient(135deg, #0f66b8 0%, #122341 100%);
        color: #fff;
        font-weight: 700;
    }

    .mv-btn--cancel {
        border: 0;
        background: none;
        color: #aaa;
        font-size: 12px;
        text-decoration: underline;
    }

    .ticket-preview-number {
        margin-left: auto;
        font-size: 11px;
        font-weight: 600;
    }

    .ticket-preview-error {
        margin-top: 8px;
        padding: 10px;
        background: #fef2f2;
        border: 1px solid #fecaca;
        border-radius: 8px;
        color: #dc2626;
        font-size: 12px;
    }

        .ticket-preview-error p {
            margin: 0;
        }

    .ticket-preview-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-top: 1px solid #f0f0f0;
        padding: 10px 18px;
        color: #64748b;
        font-size: 11px;
    }

    .mv-saved {
        color: #16a34a;
        font-weight: 600;
    }

    .mv-error-text {
        color: #dc2626;
        font-weight: 600;
    }

    .mv-text-message {
        width: fit-content;
        min-width: 0;
        max-width: 330px;
        padding: 12px 16px;
        border-radius: 6px 16px 16px 16px;
        background: #fff;
        color: #333;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        font-family: Outfit, sans-serif;
        font-size: 13px;
        font-weight: 400;
        line-height: 1.5;
        box-sizing: border-box;
        overflow-wrap: break-word;
    }

    .mv-phone-card {
        width: 330px;
        max-width: 330px;
        padding: 0;
        border-radius: 16px;
        overflow: visible;
    }

    .mv-phone-question {
        width: 100%;
        padding: 13px 16px;
        margin: 0;
        box-sizing: border-box;
        background: linear-gradient(135deg, #0f66b8 0%, #122341 100%);
        color: #fff;
        border-radius: 16px 16px 0 0;
        font-size: 13px;
        font-weight: 600;
        line-height: 1.4;
    }

    .mv-phone-row {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        padding: 16px 16px 10px;
        box-sizing: border-box;
        background: #fff;
    }

    .mv-country-dropdown {
        width: 125px;
        min-width: 125px;
        max-width: 125px;
        flex: 0 0 125px;
    }

    .mv-phone-input {
        flex: 1;
        width: 100%;
        min-width: 0;
        height: 40px;
        padding: 8px 10px;
        border: 1px solid #d7dce2;
        border-radius: 8px;
        background: #fff;
        color: #1e2d45;
        font-family: Outfit, sans-serif;
        font-size: 13px;
        outline: none;
        box-sizing: border-box;
    }

    .mv-phone-error {
        padding: 0 16px;
        margin: 0 0 5px;
        background: #fff;
        color: #dc2626;
        font-size: 11px;
    }

    .mv-phone-submit {
        width: calc(100% - 32px);
        min-height: 40px;
        margin: 4px 16px 16px;
    }

    .mv-preview-phone-row {
        position: relative;
        width: 190px;
        min-width: 190px;
        max-width: 190px;
        display: flex;
        align-items: center;
        gap: 6px;
    }

    .mv-preview-country-dropdown {
        width: 80px;
        min-width: 80px;
        max-width: 80px;
        flex: 0 0 80px;
    }

    /* Phone input must fill only the remaining space in the row */
    .mv-preview-phone-input,
    .mv-input.mv-preview-phone-input {
        flex: 1 1 0;
        width: auto;
        min-width: 0;
        max-width: none;
    }

    :deep(.mv-country-dropdown.p-dropdown) {
        width: 125px;
        min-width: 125px;
        max-width: 125px;
        height: 40px;
        border: 1px solid #d7dce2;
        border-radius: 8px;
        background: #fff;
        box-shadow: none;
    }

    :deep(.mv-preview-country-dropdown.p-dropdown) {
        width: 80px;
        min-width: 80px;
        max-width: 80px;
        height: 36px;
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        background: #fff;
        box-shadow: none;
    }

    :deep(.mv-form-dropdown.p-dropdown) {
        width: 190px;
        min-width: 190px;
        max-width: 190px;
        height: 36px;
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        background: #fff;
        box-shadow: none;
    }

    .mv-search-dropdown {
        position: relative;
        width: 190px;
        min-width: 190px;
        max-width: 190px;
        flex: 0 0 190px;
        font-family: Outfit, sans-serif;
        z-index: 2;
    }

        .mv-search-dropdown.is-open {
            z-index: 10010;
        }

    .mv-search-dropdown__trigger {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        width: 100%;
        height: 36px;
        padding: 0 9px;
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        background: #fff;
        color: #1e2d45;
        font-family: inherit;
        font-size: 12px;
        text-align: left;
        cursor: pointer;
        box-sizing: border-box;
    }

        .mv-search-dropdown__trigger:focus {
            outline: none;
            border-color: #9acfff;
            box-shadow: 0 0 0 2px rgba(15, 102, 184, 0.12);
        }

        .mv-search-dropdown__trigger:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }

        .mv-search-dropdown__trigger > span:first-child {
            min-width: 0;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
        }

        .mv-search-dropdown__trigger .is-placeholder {
            color: #98a2b3;
        }

    .mv-search-dropdown__icons {
        display: inline-flex;
        flex: 0 0 auto;
        align-items: center;
        gap: 5px;
        color: #64748b;
    }

        .mv-search-dropdown__icons svg {
            width: 15px;
            height: 15px;
            fill: none;
            stroke: currentColor;
            stroke-width: 1.8;
            stroke-linecap: round;
            stroke-linejoin: round;
        }

    .mv-search-dropdown__clear {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        color: #dc2626;
        font-size: 19px;
        line-height: 1;
        cursor: pointer;
    }

    .mv-search-dropdown__panel {
        position: absolute;
        top: calc(100% + 2px);
        left: 0;
        width: 100%;
        min-width: 190px;
        background: #fff;
        border: 1px solid #d1d5db;
        border-radius: 4px;
        box-shadow: 0 5px 16px rgba(15, 23, 42, 0.18);
        z-index: 10020;
        overflow: hidden;
    }

    .mv-search-dropdown__search {
        position: relative;
        padding: 8px;
        border-bottom: 1px solid #e5e7eb;
        background: #fff;
    }

        .mv-search-dropdown__search input {
            display: block;
            width: 100%;
            height: 36px;
            padding: 0 34px 0 10px;
            border: 1px solid #d1d5db;
            border-radius: 4px;
            outline: none;
            box-sizing: border-box;
            color: #344054;
            background: #fff;
            font-family: inherit;
            font-size: 12px;
        }

            .mv-search-dropdown__search input:focus {
                border-color: #9acfff;
                box-shadow: 0 0 0 1px #9acfff;
            }

        .mv-search-dropdown__search svg {
            position: absolute;
            right: 17px;
            top: 18px;
            width: 17px;
            height: 17px;
            fill: none;
            stroke: #667085;
            stroke-width: 1.8;
            stroke-linecap: round;
        }

    .mv-search-dropdown__options {
        max-height: 220px;
        overflow-y: auto;
        overscroll-behavior: contain;
        padding: 0;
    }

    .mv-search-dropdown__option {
        display: block;
        width: 100%;
        min-height: 44px;
        padding: 10px 12px;
        border: 0;
        background: #fff;
        color: #59616b;
        font-family: inherit;
        font-size: 12px;
        text-align: left;
        cursor: pointer;
    }

        .mv-search-dropdown__option:hover,
        .mv-search-dropdown__option.is-selected {
            background: #cfe8fb;
        }

    .mv-search-dropdown__empty {
        padding: 14px 12px;
        color: #667085;
        font-size: 12px;
    }

    :deep(.mv-country-dropdown .p-dropdown-label),
    :deep(.mv-preview-country-dropdown .p-dropdown-label),
    :deep(.mv-form-dropdown .p-dropdown-label) {
        padding: 8px 7px;
        color: #1e2d45;
        font-size: 12px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    :deep(.mv-country-dropdown .p-dropdown-trigger),
    :deep(.mv-preview-country-dropdown .p-dropdown-trigger),
    :deep(.mv-form-dropdown .p-dropdown-trigger) {
        width: 28px;
        min-width: 28px;
    }

    /* Country and State dropdown overlay fix */
    :deep(.mv-form-dropdown-panel) {
        z-index: 99999 !important;
        min-width: 190px;
        max-width: 260px;
    }

    :deep(.mv-form-dropdown-panel .p-dropdown-items-wrapper) {
        max-height: 220px;
        overflow-y: auto;
    }

    :deep(.mv-form-dropdown-panel .p-dropdown-item) {
        white-space: normal;
        overflow-wrap: anywhere;
    }

    /* Dropdown popup width fix only - functionality untouched */
    :deep(.mv-country-dropdown .p-dropdown-panel),
    :deep(.mv-preview-country-dropdown .p-dropdown-panel),
    :deep(.mv-form-dropdown .p-dropdown-panel) {
        width: 190px !important;
        min-width: 190px !important;
        max-width: 190px !important;
        z-index: 99999 !important;
    }

    :deep(.mv-country-dropdown .p-dropdown-items-wrapper),
    :deep(.mv-preview-country-dropdown .p-dropdown-items-wrapper),
    :deep(.mv-form-dropdown .p-dropdown-items-wrapper) {
        width: 100% !important;
        max-width: 100% !important;
        max-height: 210px !important;
        overflow-x: hidden !important;
    }

    :deep(.mv-country-dropdown .p-dropdown-item),
    :deep(.mv-preview-country-dropdown .p-dropdown-item),
    :deep(.mv-form-dropdown .p-dropdown-item) {
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box;
        padding: 8px 10px !important;
        font-size: 12px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    .mv-multi {
        position: relative;
    }

    .mv-multi__trigger {
        width: 100%;
        min-height: 36px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 6px;
        padding: 7px 9px;
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        background: #fff;
        color: #1e2d45;
        font-family: Outfit, sans-serif;
        font-size: 12px;
        text-align: left;
        cursor: pointer;
        box-sizing: border-box;
    }

        .mv-multi__trigger > span:first-child {
            min-width: 0;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
        }

    .mv-chevron {
        flex: 0 0 auto;
        color: #64748b;
        font-size: 10px;
    }

    .mv-multi__panel {
        position: absolute;
        top: calc(100% + 4px);
        right: 0;
        z-index: 1000;
        width: 220px;
        max-height: 190px;
        overflow-y: auto;
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        background: #fff;
        box-shadow: 0 6px 18px rgba(15, 35, 65, 0.14);
    }

    .mv-check {
        min-height: 34px;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 7px 10px;
        border-bottom: 1px solid #f1f5f9;
        color: #1e2d45;
        font-size: 12px;
        cursor: pointer;
        box-sizing: border-box;
    }

    .mv-check--all {
        font-weight: 700;
        background: #f8fafc;
    }

    .mv-check input {
        margin: 0;
        flex: 0 0 auto;
    }

    .mv-empty-inline {
        padding: 10px;
        color: #999;
        font-size: 11px;
        text-align: center;
    }

    .mv-error {
        margin: 8px 0 0;
        color: #dc2626;
        font-size: 11px;
    }

    .mv-list-body {
        padding: 0 16px 8px;
    }

    .mv-empty {
        padding: 16px 0;
        color: #999;
        font-size: 12px;
        text-align: center;
    }

    .mv-list-item {
        padding: 10px 0;
        border-bottom: 1px solid #e5e7eb;
    }

    .mv-list-item--last {
        border-bottom: 0;
    }

    .mv-list-top {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 8px;
    }

    .mv-list-main {
        flex: 1;
        min-width: 0;
    }

    .mv-list-profile {
        display: flex;
        align-items: center;
        gap: 10px;
        flex: 1;
        min-width: 0;
    }

    .mv-list-avatar {
        width: 30px;
        height: 30px;
        min-width: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        color: #fff;
        font-size: 10px;
        font-weight: 700;
        line-height: 1;
        text-transform: uppercase;
    }

    .mv-list-title {
        color: #1e2d45;
        font-size: 13px;
        font-weight: 600;
    }

    .mv-list-meta {
        margin-top: 3px;
        color: #64748b;
        font-size: 11px;
        overflow-wrap: anywhere;
    }

    .mv-list-actions {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 5px;
        flex: 0 0 auto;
    }

    .mv-detail-btn,
    .mv-status-btn {
        border-radius: 12px;
        padding: 3px 9px;
        background: #fff;
        font-family: Outfit, sans-serif;
        font-size: 10px;
        font-weight: 600;
        cursor: pointer;
    }

    .mv-detail-btn {
        border: 1px solid #cbd5e1;
        color: #1e2d45;
    }

    .mv-status-btn--active {
        border: 1px solid #86efac;
        color: #16803a;
        background: #f0fdf4;
    }

    .mv-status-btn--inactive {
        border: 1px solid #fca5a5;
        color: #dc2626;
        background: #fff7f7;
    }

    .mv-vendor-details {
        margin-top: 10px;
        padding: 10px 12px;
        border: 1px solid #e5e7eb;
        border-radius: 9px;
        background: #f8fafc;
    }

    .mv-detail-row {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 12px;
        padding: 6px 0;
        border-bottom: 1px solid #e5e7eb;
        font-size: 11px;
    }

        .mv-detail-row:last-child {
            border-bottom: 0;
        }

        .mv-detail-row > span {
            min-width: 72px;
            color: #667085;
            font-weight: 600;
        }

        .mv-detail-row > strong {
            min-width: 0;
            color: #1e2d45;
            font-weight: 600;
            text-align: right;
            overflow-wrap: anywhere;
        }

            .mv-detail-row > strong.mv-phone-value {
                white-space: nowrap;
                overflow-wrap: normal;
                word-break: keep-all;
                flex-shrink: 0;
            }

    @media (max-width: 480px) {
        .mv-summary-card,
        .ticket-preview-card {
            min-width: 0;
            width: 100%;
            max-width: 330px;
        }

        .mv-text-message {
            max-width: 300px;
        }

        .mv-phone-card {
            width: 300px;
            max-width: 300px;
        }

        .mv-country-dropdown {
            width: 115px;
            min-width: 115px;
            max-width: 115px;
            flex-basis: 115px;
        }

        :deep(.mv-country-dropdown.p-dropdown) {
            width: 115px;
            min-width: 115px;
            max-width: 115px;
        }

        .mv-label,
        .tp-label {
            width: 84px;
            min-width: 84px;
        }

        .mv-input,
        .mv-form-dropdown,
        .mv-multi,
        .mv-preview-phone-row {
            width: 175px;
            min-width: 175px;
            max-width: 175px;
        }

            /* Keep the phone input flexible on mobile (beats .mv-input above) */
            .mv-input.mv-preview-phone-input {
                flex: 1 1 0;
                width: auto;
                min-width: 0;
                max-width: none;
            }

        :deep(.mv-form-dropdown.p-dropdown) {
            width: 175px;
            min-width: 175px;
            max-width: 175px;
        }

        .mv-search-dropdown {
            width: 175px;
            min-width: 175px;
            max-width: 175px;
            flex-basis: 175px;
        }

        .mv-preview-country-dropdown {
            width: 76px;
            min-width: 76px;
            max-width: 76px;
            flex-basis: 76px;
        }

        :deep(.mv-preview-country-dropdown.p-dropdown) {
            width: 76px;
            min-width: 76px;
            max-width: 76px;
        }

        /* FIX: a stray ".mv-label" selector used to be glued to this rule */
        .mv-multi__panel {
            width: 205px;
            max-width: calc(100vw - 50px);
        }
    }

    /* ONLY mandatory asterisk color fix - no functionality changes */
    .mv-label .mv-required,
    :deep(.mv-label .mv-required),
    .manage-vendors .mv-label .mv-required,
    :deep(.manage-vendors .mv-label .mv-required) {
        color: #dc2626 !important;
        -webkit-text-fill-color: #dc2626 !important;
        font-weight: 700 !important;
        display: inline-block !important;
        opacity: 1 !important;
        margin-left: 2px !important;
    }
</style>